import { useEffect, useState } from "react";
import { Plus, X, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";
import { StatusChip, BOOKING_STATUS, ACCENT } from "./ptShared";

const WEEKDAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];

/* PT Trajects — terugkerende PT-reeksen in één keer plannen (spec 45–49) */
export default function AdminPTTrajects({ openClient }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null); // null = gesloten
  const load = () => api.get("/pt/admin/trajects").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="pt-trajects">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="chip text-[#A3A3A3]">— PT-TRAJECTEN</div>
        <div className="flex gap-2">
          <button onClick={() => setForm({ kind: "package" })} className="chip px-4 py-2.5 border inline-flex items-center gap-2 transition-colors" style={{ borderColor: ACCENT, color: ACCENT }} data-testid="pt-traject-new"><Plus size={13}/> New traject</button>
          <button onClick={() => setForm({ kind: "manual" })} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#DDD6CB] inline-flex items-center gap-2" data-testid="pt-traject-new-manual"><Plus size={13}/> Manual traject</button>
        </div>
      </div>
      {form && <TrajectForm kind={form.kind} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
        : items.length === 0 ? <div className="border border-[#F5F3F0]/15 p-8 text-center chip text-[#4A4A4A]" data-testid="pt-trajects-empty">Nog geen trajecten. Plan een terugkerende reeks — bijvoorbeeld elke maandag 4 weken.</div>
        : <div className="space-y-3">{items.map((t) => <TrajectCard key={t.id} t={t} onDone={load} openClient={openClient} />)}</div>}
    </div>
  );
}

function TrajectForm({ kind, onClose, onDone }) {
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [f, setF] = useState({ email: "", package_id: "", start_date: "", weeks: 4, weekday: 0, time: "18:00", note: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/pt/admin/clients").then(({ data }) => setClients(data.items)).catch(() => {});
    api.get("/pt/admin/packages").then(({ data }) => setPackages(data.items.filter((p) => p.remaining > 0))).catch(() => {});
  }, []);
  const clientPackages = packages.filter((p) => p.email === f.email);

  const doPreview = async () => {
    try {
      const { data } = await api.post("/pt/admin/trajects/preview", { start_date: f.start_date, weeks: f.weeks, weekday: f.weekday, time: f.time });
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Preview mislukt"); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pt/admin/trajects", { ...f, package_id: f.package_id || undefined });
      toast.success(`${data.sessions_created} sessies gepland${data.conflicts.length ? ` — ${data.conflicts.length} conflict overgeslagen` : ""}${!f.package_id ? " (manual traject — geen pakket gekoppeld)" : ""}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Aanmaken mislukt"); }
    setBusy(false);
  };

  return (
    <div className="border border-[#F5F3F0]/20 p-5 mb-8" data-testid="pt-traject-form">
      <div className="flex items-start justify-between mb-4">
        <div className="chip" style={{ color: ACCENT }}>// {kind === "manual" ? "MANUAL TRAJECT (zonder pakket)" : "NEW TRAJECT (met pakket)"}</div>
        <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="pt-traject-close"><X size={16}/></button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div><label className="chip text-[#A3A3A3]">Klant*</label>
          <select value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value, package_id: "" }))} className="editorial-input dark" data-testid="pt-traject-client">
            <option value="" className="bg-[#0A0A0A]">Kies klant…</option>
            {clients.map((c) => <option key={c.email} value={c.email} className="bg-[#0A0A0A]">{c.name} — {c.email}</option>)}
          </select>
        </div>
        {kind === "package" && (
          <div><label className="chip text-[#A3A3A3]">Pakket</label>
            <select value={f.package_id} onChange={(e) => setF((p) => ({ ...p, package_id: e.target.value }))} className="editorial-input dark" data-testid="pt-traject-package">
              <option value="" className="bg-[#0A0A0A]">{f.email ? (clientPackages.length ? "Kies pakket…" : "Geen actief pakket") : "Kies eerst een klant"}</option>
              {clientPackages.map((p) => <option key={p.id} value={p.id} className="bg-[#0A0A0A]">{p.label} — {p.remaining} resterend</option>)}
            </select>
          </div>
        )}
        <div><label className="chip text-[#A3A3A3]">Start date*</label><input type="date" value={f.start_date} onChange={(e) => { setF((p) => ({ ...p, start_date: e.target.value })); setPreview(null); }} className="editorial-input dark" data-testid="pt-traject-start" /></div>
        <div><label className="chip text-[#A3A3A3]">Repeat*</label>
          <select value={f.weekday} onChange={(e) => { setF((p) => ({ ...p, weekday: parseInt(e.target.value) })); setPreview(null); }} className="editorial-input dark" data-testid="pt-traject-weekday">
            {WEEKDAYS.map((w, i) => <option key={i} value={i} className="bg-[#0A0A0A]">Elke {w.toLowerCase()}</option>)}
          </select>
        </div>
        <div><label className="chip text-[#A3A3A3]">Weeks*</label><input type="number" min="1" max="24" value={f.weeks} onChange={(e) => { setF((p) => ({ ...p, weeks: parseInt(e.target.value) || 1 })); setPreview(null); }} className="editorial-input dark" data-testid="pt-traject-weeks" /></div>
        <div><label className="chip text-[#A3A3A3]">Time*</label><input type="time" step="3600" value={f.time} onChange={(e) => { setF((p) => ({ ...p, time: e.target.value })); setPreview(null); }} className="editorial-input dark" data-testid="pt-traject-time" /></div>
      </div>
      <div className="mt-4"><label className="chip text-[#A3A3A3]">Notitie (optioneel)</label><input value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} className="editorial-input dark" data-testid="pt-traject-note" /></div>
      <div className="flex flex-wrap gap-3 mt-5 items-center">
        <button onClick={doPreview} disabled={!f.start_date} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#DDD6CB]" data-testid="pt-traject-preview"><CalendarClock size={13} className="inline -mt-0.5"/> Controleer conflicten</button>
        {preview && (
          <span className="chip" data-testid="pt-traject-preview-result">
            <span style={{ color: "#4ADE80" }}>{preview.available_count} sessions available</span>
            {preview.conflict_count > 0 && <span style={{ color: "#F87171" }}> · {preview.conflict_count} scheduling conflict{preview.conflict_count > 1 ? "s" : ""}</span>}
          </span>
        )}
        <button onClick={create} disabled={busy || !f.email || !f.start_date} className="chip px-5 py-2.5 disabled:opacity-40" style={{ background: ACCENT, color: "#0A0A0A" }} data-testid="pt-traject-create">
          {busy ? "…" : "TRAJECT PLANNEN"}
        </button>
      </div>
      {kind === "package" && f.email && !f.package_id && (
        <p className="chip text-[#D4A93A] mt-3" data-testid="pt-traject-nopackage-hint">Geen pakket gekozen — dit wordt een MANUAL TRAJECT (geen pakket-afschrijving).</p>
      )}
      {preview && preview.conflict_count > 0 && (
        <p className="chip text-[#F87171] mt-3">Conflicterende datums worden overgeslagen — dubbele afspraken worden nooit aangemaakt.</p>
      )}
    </div>
  );
}

function TrajectCard({ t, onDone, openClient }) {
  const [editFor, setEditFor] = useState(null);
  const [scope, setScope] = useState("one");
  const [newTime, setNewTime] = useState("");
  const [newDate, setNewDate] = useState("");
  const [showExtend, setShowExtend] = useState(false);
  const [extWeeks, setExtWeeks] = useState(4);
  const [busy, setBusy] = useState(false);
  const upcoming = (t.sessions || []).filter((s) => s.status === "approved");

  const applyExtend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pt/admin/trajects/${t.id}/extend`, { weeks: extWeeks });
      toast.success(`${data.sessions_created} sessie(s) toegevoegd${data.conflicts.length ? ` — ${data.conflicts.length} conflict(en) overgeslagen` : ""}`);
      setShowExtend(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Verlengen mislukt"); }
    setBusy(false);
  };

  const applyEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/pt/admin/trajects/${t.id}/sessions`, {
        booking_id: editFor.id, scope, time: newTime || undefined, date: scope === "one" ? (newDate || undefined) : undefined,
      });
      toast.success(`${data.updated} sessie(s) aangepast${data.conflicts.length ? ` — ${data.conflicts.length} conflict overgeslagen` : ""}`);
      setEditFor(null); setNewTime(""); setNewDate("");
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Aanpassen mislukt"); }
    setBusy(false);
  };

  return (
    <div className="border border-[#F5F3F0]/15 p-5" data-testid={`pt-traject-${t.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => openClient(t.email)} className="font-display uppercase text-lg hover:underline underline-offset-4" data-testid={`pt-traject-client-${t.id}`}>{t.email}</button>
        <span className="chip px-2 py-0.5 border" style={{ borderColor: `${ACCENT}66`, color: ACCENT }}>{t.kind === "manual" ? "MANUAL TRAJECT" : "TRAJECT + PAKKET"}</span>
        <span className="chip text-[#A3A3A3]">elke {WEEKDAYS[t.weekday].toLowerCase()} · {t.time} · {t.weeks} weken vanaf {t.start_date}</span>
        <span className="chip text-[#DDD6CB] ml-auto" data-testid={`pt-traject-progress-${t.id}`}>{t.completed} afgerond · {t.scheduled} gepland</span>
        <button onClick={() => setShowExtend(!showExtend)} className="chip px-2.5 py-1 border border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors" data-testid={`pt-traject-extend-${t.id}`}>+ Verleng</button>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {(t.sessions || []).map((s) => (
          <button key={s.id} onClick={() => { if (s.status === "approved") setEditFor(editFor?.id === s.id ? null : s); }}
            className="chip px-2.5 py-1.5 border transition-colors"
            style={{ borderColor: s.status === "completed" ? "#4ADE8055" : s.status === "approved" ? "#F5F3F033" : "#F8717155", color: s.status === "completed" ? "#4ADE80" : s.status === "approved" ? "#DDD6CB" : "#F87171" }}
            data-testid={`pt-traject-session-${s.id}`}>
            {s.date.slice(5)} {s.time}{s.status === "completed" ? " ✓" : ""}
          </button>
        ))}
      </div>
      {showExtend && (
        <div className="border border-[#F5F3F0]/15 p-4 mt-4" data-testid="pt-traject-extend-panel">
          <div className="chip text-[#A3A3A3] mb-3">TRAJECT VERLENGEN — elke {WEEKDAYS[t.weekday].toLowerCase()} {t.time}, aansluitend na de laatste sessie</div>
          <div className="flex flex-wrap gap-3 items-end">
            <div><label className="chip text-[#A3A3A3]">Extra weken</label><input type="number" min="1" max="24" value={extWeeks} onChange={(e) => setExtWeeks(parseInt(e.target.value) || 1)} className="editorial-input dark w-24" data-testid="pt-traject-extend-weeks" /></div>
            <button onClick={applyExtend} disabled={busy} className="chip px-4 py-2.5 disabled:opacity-40" style={{ background: ACCENT, color: "#0A0A0A" }} data-testid="pt-traject-extend-apply">{busy ? "…" : "Verleng"}</button>
            <span className="chip text-[#4A4A4A]">Conflicterende datums worden overgeslagen{t.package_id ? " · sessies gaan van het gekoppelde pakket af" : ""}.</span>
          </div>
        </div>
      )}
      {editFor && (
        <div className="border border-[#F5F3F0]/15 p-4 mt-4" data-testid="pt-traject-edit">
          <div className="chip text-[#A3A3A3] mb-3">SESSIE {editFor.date} · {editFor.time} AANPASSEN</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[["one", "Change this session only"], ["future", "Change this and all future sessions"]].map(([v, label]) => (
              <button key={v} onClick={() => setScope(v)} className="chip px-3 py-2 border transition-colors"
                style={scope === v ? { background: ACCENT, color: "#0A0A0A", borderColor: ACCENT } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
                data-testid={`pt-traject-scope-${v}`}>{label}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            {scope === "one" && <div><label className="chip text-[#A3A3A3]">Nieuwe datum</label><input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="editorial-input dark" data-testid="pt-traject-newdate" /></div>}
            <div><label className="chip text-[#A3A3A3]">Nieuwe tijd</label><input type="time" step="3600" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="editorial-input dark" data-testid="pt-traject-newtime" /></div>
            <button onClick={applyEdit} disabled={busy || (!newTime && !newDate)} className="chip px-4 py-2.5 disabled:opacity-40" style={{ background: ACCENT, color: "#0A0A0A" }} data-testid="pt-traject-apply">{busy ? "…" : "Toepassen"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
